/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {
    Component, ElementRef, HostBinding,
    HostListener, OnDestroy, Input, OnInit
} from '@angular/core';
import {EditorView} from 'prosemirror-view';
import {Subscription} from 'rxjs';
import Icon from '../../../icons';
import {FcAgEditorService} from '../../../editor.service';
import {MenuService} from '../menu.service';
import {TextColor, TextBackgroundColor} from '../MenuCommands';

type Command = typeof TextColor | typeof TextBackgroundColor;

/**
 *
 */
@Component({
    selector: 'fc-ag-color-picker',
    templateUrl: './color-picker.component.html',
    styleUrls: ['./color-picker.component.scss']
})
export class ColorPickerComponent implements OnInit, OnDestroy {
    /**
     * Color list.
     */
    @Input() presets: string[][];

    /**
     * Color type.
     */
    @Input() type: string;

    /**
     * Default constructor.
     * 
     * @param el Element reference.
     * @param menuService Menu service.
     * @param fcAgEditorService FcAgEditorService.
     */
    constructor(
        private el: ElementRef,
        private menuService: MenuService,
        private fcAgEditorService: FcAgEditorService
    ) {
    }

    /**
     * Is valid binding.
     * 
     * @returns Boolean.
     */
    @HostBinding('class.FcAgEditor__MenuItem--Active') get valid(): boolean {
        return this.isActive || this.showPopup;
    }

    /**
     * Is disabled binding.
     * 
     * @returns Boolean.
     */
    @HostBinding('class.FcAgEditor--Disabled') get disabled(): boolean {
        return !this.canExecute;
    }

    /**
     * Getter for title.
     * 
     * @returns String.
     */
    get title(): string {
        return this.getLabel(this.type === 'text_color' ? 'text_color' : 'background_color');
    }

    /**
     * Getter for icon.
     * 
     * @returns String.
     */
    get icon(): string {
        return Icon.get(this.type === 'text_color' ? 'text_color' : 'color_fill');
    }

    private get command(): Command {
        return this.type === 'text_color' ? TextColor : TextBackgroundColor;
    }

    private updateSubscription: Subscription;

    private editorView: EditorView;

    /**
     * Is showing popup.
     */
    showPopup = false;

    /**
     * Is active.
     */
    isActive = false;

    /**
     * Active colors.
     */
    activeColors: string[] = [];

    private canExecute = true;

    /**
     * Get contrast YIQ.
     * 
     * @param hexcolor Hex color.
     * @returns String.
     */
    getContrastYIQ(hexcolor: string): string {
        const color = hexcolor.replace('#', '');
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? 'black' : 'white';
    }

    /**
     * Document click binding.
     * 
     * @param e Mouse event.
     */
    @HostListener('document:mousedown', ['$event']) onDocumentClick(e: MouseEvent): void {
        if (!this.el.nativeElement.contains(e.target) && this.showPopup) {
            this.hidePopup();
        }
    }

    private hidePopup(): void {
        this.showPopup = false;
    }

    /**
     * Toggle popup.
     * 
     * @param e Mouse Event.
     */
    togglePopup(e: MouseEvent): void {
        e.preventDefault();

        if (e.button !== 0) {
            return;
        }

        this.showPopup = !this.showPopup;
    }

    /**
     * Remove color.
     * 
     * @param e Mouse event.
     */
    remove(e: MouseEvent): void {
        e.preventDefault();
        const {state, dispatch} = this.editorView;

        this.command.remove()(state, dispatch);
        this.hidePopup();
    }

    /**
     * Track by index.
     * 
     * @param index Index.
     * @returns Number.
     */
    trackByIndex(index: number): number {
        return index;
    }

    /**
     * On color select callback.
     * 
     * @param e Mouse Event.
     * @param color Color.
     */
    onColorSelect(e: MouseEvent, color: string): void {
        e.preventDefault();

        if (e.button !== 0) {
            return;
        }

        const {state, dispatch} = this.editorView;

        if (this.type === 'text_color') {
            const attrs = {color};
            this.command.apply(attrs)(state, dispatch);
        } else {
            const attrs = {backgroundColor: color};
            this.command.apply(attrs)(state, dispatch);
        }

        if (!this.editorView.hasFocus()) {
            this.editorView.focus();
        }

        this.hidePopup();
    }

    private update = (view: EditorView) => {
        const {state} = view;
        this.canExecute = this.command.canExecute(state);
        this.isActive = this.command.isActive(state);
        this.activeColors = [];

        if (this.isActive) {
            this.activeColors = this.command.getActiveColors(state);
        }
    }

    /**
     * Get label.
     * 
     * @param key String.
     * @returns String.
     */
    getLabel(key: string): string {
        return this.fcAgEditorService.locals.get(key);
    }

    /**
     * Angular life cycle.
     */
    ngOnInit(): void {
        this.editorView = this.menuService.editor.view;

        this.updateSubscription = this.menuService.editor.update.subscribe((view: EditorView) => {
            this.update(view);
        });
    }

    /**
     * Angular life cycle.
     */
    ngOnDestroy(): void {
        this.updateSubscription.unsubscribe();
    }
}
